import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Row, Col, Button, Input, Modal, Skeleton, Tabs, Space, DatePicker } from "antd";
import dayjs from "dayjs";
import Select from "react-select";

let BASE_STATE_JOB = { name: null, comments: null, item: null, items_xref: null, job_status: null, job_statuses_xref: null, promised_by: null, form_due_date: null, clients_xref: null };

/*
Prop api
open: boolean, 
client_id: int,
refresh: function
*/

function ModalAddJob(props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState(1200);

  const [job, setJob] = useState(BASE_STATE_JOB);

  const [company, setCompany] = useState(null);
  const [client, setClient] = useState(null);
  const [items, setItems] = useState([]);
  const [jobStatuses, setJobStatuses] = useState([]);

  const focusFirst = useRef(null);

  const GetClient = async (id, show = false) => {
    setModalOpen(true);
    let response = await props.AxiosRequest("/api/clients/get", { cid: id });
    setClient(response.data.client);
    setJob({ ...job, clients_xref: response.data.client.id });
    BASE_STATE_JOB.clients_xref = response.data.client.id;
  };
  const GetCompany = async () => {
    let response = await props.AxiosRequest("/api/companies/get");
    setCompany(response.data.company);
    setJob({ ...job, promised_by: dayjs().add(response.data.company.default_job_days_due, "day") });
    BASE_STATE_JOB.promised_by = dayjs().add(response.data.company.default_job_days_due, "day");
  };
  const GetItems = async () => {
    let response = await props.AxiosRequest("/api/items/get");
    setItems(
      response.data.items.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };
  const GetJobStatuses = async () => {
    let response = await props.AxiosRequest("/api/job-statuses/get");
    let statuses = response.data.job_statuses.map((m) => {
      return { ...m, label: m.name, value: m.id };
    });
    setJobStatuses(statuses);
    let start_status = statuses.find((f) => f.start === 1);
    if (start_status) {
      setJob({ ...job, job_status: start_status, job_statuses_xref: start_status.id });
      BASE_STATE_JOB.job_status = start_status;
      BASE_STATE_JOB.job_statuses_xref = start_status.id;
    }
  };
  const AddJob = async () => {
    if (job?.name?.length === 0) return props.MakeNotification("info", "Please enter the job name.");
    if (!job?.items_xref || !job?.name || !job?.job_statuses_xref || !job?.promised_by) return props.MakeNotification("info", "Please fill in all required fields.");
    let response = await props.AxiosRequest("/api/jobs/insert", { ...job });
    props.setAddJobProps({ ...props.addJobProps, open: false, refresh: true, new_job_id: response.data.new_job_id });
    setJob({ ...BASE_STATE_JOB });
  };

  useEffect(() => {
    const resize = () => {
      const maxSize = Math.min(900, window.innerWidth);
      setModalWidth(maxSize);
    };
    window.addEventListener("resize", resize);
  });

  useEffect(() => {
    if (props.addJobProps.open && props.addJobProps.client_id) {
      GetClient(props.addJobProps.client_id);
    }
    setModalOpen(props.addJobProps.open);
  }, [props.addJobProps.open, props.addJobProps.client_id]);

  useEffect(() => {
    GetItems();
    GetJobStatuses();
    GetCompany();
  }, []);

  return (
    <>
      <Modal
        afterOpenChange={(open) => open && focusFirst.current?.focus()}
        title={client?.name ? "Add A New Job For " + client.name : "Add A New Job"}
        centered
        destroyOnClose={true}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setJob({ ...BASE_STATE_JOB });
          props.setAddJobProps({ ...props.addJobProps, open: false, client_id: null });
        }}
        footer={
          <>
            <Button
              onClick={() => {
                setModalOpen(false);
                props.setAddJobProps({ ...props.addJobProps, open: false, client_id: null });
              }}
            >
              Cancel
            </Button>
            <Button type="primary" onClick={AddJob}>
              Add Job
            </Button>
          </>
        }
        width={modalWidth}
      >
        {job ? (
          <Row gutter={[{ xs: 8, sm: 16, md: 16, lg: 16 }, 8]}>
            <Col span={12}>
              <label className="col-form-label-sm">Job Name *</label>
              <Input
                ref={focusFirst}
                value={job?.name}
                placeholder="Bookkeeping, 1040, Quarterly Report, etc"
                onChange={(e) => {
                  setJob({ ...job, name: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Item *</label>
              <Select
                value={job.item}
                options={items}
                onChange={(e) => {
                  setJob({ ...job, item: e, items_xref: e.value });
                }}
              />
            </Col>
            <Col span={24}>
              <label className="col-form-label-sm">Comments</label>
              <Input.TextArea
                value={job.comments}
                onChange={(e) => {
                  setJob({ ...job, comments: e.target.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Job Status *</label>
              <Select
                value={job.job_status}
                options={jobStatuses}
                onChange={(e) => {
                  setJob({ ...job, job_status: e, job_statuses_xref: e.value });
                }}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Due Date *</label>
              <DatePicker
                format={"MM/DD/YYYY"}
                style={{ width: "100%" }}
                allowClear={false}
                value={job.promised_by}
                onChange={(e) => {
                  setJob({ ...job, promised_by: e });
                }}
              />
            </Col>
            <Col span={8}>
              <label className="col-form-label-sm">Form Due Date</label>
              <DatePicker
                format={"MM/DD/YYYY"}
                style={{ width: "100%" }}
                value={job.form_due_date}
                onChange={(e) => {
                  setJob({ ...job, form_due_date: e });
                }}
              />
            </Col>
          </Row>
        ) : (
          <Skeleton active />
        )}
      </Modal>
    </>
  );
}

export default ModalAddJob;
